import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  /** Router link; omit/null for the current (non-clickable) page. */
  url?: string | null;
}

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  'listings-and-bookings': 'Listings & Bookings',
  'broker-manager': 'Broker Manager',
  'simple-loads': 'Simple Loads',
  'advanced-loads': 'Advanced Loads',
  'invoices-payments': 'Invoices & Payments',
  customers: 'Customers',
  report: 'Report',
  'instant-quote': 'Instant Quote',
  'instant-rate-manager': 'Rate Manager',
  'location-settings': 'Location Settings',
  'style-guide': 'Style Guide',
  settings: 'Settings',
  financials: 'Financials',
  esg: 'ESG',
  'multi-listing': 'Multi Listing',
  'invoices-matching': 'Invoices Matching',
  'create-customer': 'Create Customer',
  integrations: 'Integrations',
  payments: 'Payments',
  quotes: 'Quotes',
  'daily-planner': 'Daily Planner',
  'allocator-view': 'Daily Planner',
  'my-jobs': 'My Jobs',
  'carbon-dashboard': 'Carbon Dashboard',
  'access-denied': 'Access Denied',
};

// Only exist as prefixes of deeper routes, so linking to them alone hits the
// `**` (CMS/not-found) route. Shown as labels but rendered as plain text.
const NON_NAVIGABLE_SEGMENTS = new Set<string>([
  'broker-manager',
  'payments',
  'view',
  'pod-upload',
]);

const NUMERIC_SEGMENT = /^\d+$/;
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_TOKEN_SEGMENT = /^[A-Za-z0-9._~-]{16,}$/;

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
  ],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
})
export class BreadcrumbComponent implements OnInit, OnChanges, OnDestroy {
  @Input() homeLabel = 'Home';
  @Input() homeUrl = '/dashboard';

  /** Explicit trail; overrides URL derivation. Home is still prepended. */
  @Input() set items(value: BreadcrumbItem[] | null | undefined) {
    this._override = value ?? null;
    this.buildCrumbs();
  }

  @Input() showHome = true;

  crumbs: BreadcrumbItem[] = [];

  private trailLength = 0;
  private _override: BreadcrumbItem[] | null = null;
  private destroy$ = new Subject<void>();

  // Hide when only the Home root would show.
  get visible(): boolean {
    return this.trailLength > 0;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.buildCrumbs();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.buildCrumbs());
  }

  ngOnChanges(): void {
    this.buildCrumbs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildCrumbs(): void {
    const home: BreadcrumbItem[] = this.showHome
      ? [
          { label: this.homeLabel, url: this.homeUrl },
        ]
      : [];

    if (this._override) {
      this.trailLength = this._override.length;
      this.crumbs = [
        ...home,
        ...this._override,
      ];
      return;
    }

    const trail = this.crumbsFromUrl();
    this.trailLength = trail.length;
    this.crumbs = [
      ...home,
      ...trail,
    ];
  }

  private crumbsFromUrl(): BreadcrumbItem[] {
    // parseUrl drops query/fragment, matrix params and outlets, and decodes.
    const segments = this.router.parseUrl(this.router.url).root.children['primary']?.segments ?? [];
    const routeLabels = this.routeBreadcrumbLabels();

    let url = '';
    const crumbs: BreadcrumbItem[] = [];

    segments.forEach((segment) => {
      const path = segment.path;
      url += `/${path}`;

      const routeLabel = routeLabels.get(url);
      if (routeLabel) {
        crumbs.push({ label: routeLabel, url });
        return;
      }

      // Drop unlabelled ids/tokens so raw values never surface as labels.
      const knownLabel = SEGMENT_LABELS[path];
      if (!knownLabel && this.isDynamicSegment(path)) {
        return;
      }

      const label = knownLabel ?? this.labelFor(path);
      const navigable = !NON_NAVIGABLE_SEGMENTS.has(path);
      crumbs.push({ label, url: navigable ? url : null });
    });

    return this.markLast(crumbs);
  }

  // Reads each route's own config, not inherited snapshot data, so an
  // empty-path child of a lazy module doesn't duplicate its parent's label.
  private routeBreadcrumbLabels(): Map<string, string> {
    const labels = new Map<string, string>();
    let current: ActivatedRoute | null = this.route.root;
    let url = '';

    while (current) {
      const segment = current.snapshot.url.map((s) => s.path).join('/');
      if (segment) {
        url += `/${segment}`;
      }
      const label = current.routeConfig?.data?.['breadcrumb'];
      if (label && segment) {
        labels.set(url, label);
      }
      current = current.firstChild;
    }

    return labels;
  }

  private markLast(crumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    if (crumbs.length) {
      crumbs[crumbs.length - 1] = {
        label: crumbs[crumbs.length - 1].label,
        url: null,
      };
    }
    return crumbs;
  }

  private isDynamicSegment(segment: string): boolean {
    return NUMERIC_SEGMENT.test(segment) || UUID_SEGMENT.test(segment) || LONG_TOKEN_SEGMENT.test(segment);
  }

  private labelFor(segment: string): string {
    return SEGMENT_LABELS[segment] || segment.replace(/[-_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
